import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {GatewaySignals} from '@models/adminstration/gateway/gateway-configuration/gateway-signals';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ModbusSetting} from '@models/adminstration/gateway/gateway-configuration/modbus-setting';
import {ModbusSlave} from '@models/adminstration/gateway/gateway-configuration/modbus-slave';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {GatewayImportExportService} from '@services/administration/gateway/import-export/gateway-import-export.service';
import {
  BAUD_RATES,
  DEFAULT_BAUD_RATE,
  DEFAULT_DATA_BITS,
  DEFAULT_PARITY,
  DEFAULT_STOP_BITS,
  GATEWAY_CONFIG_DATATYPE,
  MAX_NUMBER_OF_REGISTERS,
  MODBUS_TYPE_OF_DATA,
  PARITY
} from '@constants/administration/gateway/gateway-constants';
import {ConfirmationDialogService} from '@services/confirmation-dialog.service';
import {ModbusSerialPort} from '@models/adminstration/gateway/gateway-configuration/modbus-serial-port';
import {ModbusRegister} from '@models/adminstration/gateway/gateway-configuration/modbus-register';
import {ModbusSignal} from '@models/adminstration/gateway/gateway-configuration/modbus-signal';
import {DATA_TYPE} from '@constants/data-type.constant';

@Component({
    selector: 'scitis-admin-gateway-modbus',
    templateUrl: './gateway-modbus.component.html',
    styleUrls: ['./gateway-modbus.component.scss'],
    standalone: false
})
export class GatewayModbusComponent implements OnInit, OnChanges {

  @Input()
  gateway: Gateway = new Gateway();

  @Input()
  isAdminRole: boolean = false;

  @Input()
  isSuperAdminRole: boolean = false;

  @Input()
  isTechAdminRole: boolean = false;

  @Input()
  gatewaySignals: GatewaySignals[] = new Array<GatewaySignals>();

  @Output()
  pushToIoTCoreEvent = new EventEmitter<any>();

  @Output()
  saveConfigurationEvent = new EventEmitter<any>();

  @ViewChild('uploader', {static: true}) input: ElementRef | undefined;

  isAdminOrSuperAdminOrTechAdmin: boolean = false;
  signalFormIsValid: boolean = true;
  registerFormIsValid: boolean = true;
  slaveFormIsValid: boolean = true;
  name: string = '';
  jsonType: string = '';
  modbusForm: UntypedFormGroup = new UntypedFormGroup({});
  settings: ModbusSetting = new ModbusSetting();
  baudRates = BAUD_RATES;
  parity = PARITY;
  signalNames: string[] = [];
  formGenerated: boolean = false;

  DATA_BITS_RANGE_MIN = 5;
  DATA_BITS_RANGE_MAX = 9;
  STOP_BITS_RANGE_MIN = 0;
  STOP_BITS_RANGE_MAX = 3;
  POLL_TIME_MIN = 100;
  private READ_CODE_RANGE_MIN = 3;
  private PARITY_OPTIONS = [0, 1, 2];
  private DATA_TYPE_MAX_VALUE = 4294967295;
  private SHORT_DATA_TYPE_MAX_VALUE = 65535;
  private COMPONENT_NAME = 'GatewayModbusComponent';

  private invalidTypeOfDataMessage: string = '';
  private maxRegistersReachedMessage: string = '';

  private readonly noSlaveDefinitionMessage: string = '';
  private readonly undefinedConfigurationMessage: string = '';
  private readonly undefinedConnectorMessage: string = '';
  private readonly slaveDuplicatesMessage: string = '';
  private readonly registerDuplicatesMessage: string = '';
  private readonly signalsDuplicatesMessage: string = '';
  private readonly duplicatedItemsMessage: string = '';
  private readonly errorTitle: string = '';
  private readonly cancelButton: string = '';
  private readonly importSuccessMessage: string = '';

  constructor(private notification: NotificationService,
              private translate: TranslateService,
              private importExportService: GatewayImportExportService,
              private confirmationDialogService: ConfirmationDialogService) {
    this.undefinedConfigurationMessage = this.translate.instant('The configuration is undefined');
    this.undefinedConnectorMessage = this.translate.instant('The connector data type is undefined');
    this.invalidTypeOfDataMessage = this.translate.instant('The imported configuration type of data %1 is different than the required one %2');
    this.noSlaveDefinitionMessage = this.translate.instant('The uploaded MODBUS configuration has no slave definitions');
    this.maxRegistersReachedMessage = this.translate.instant('The uploaded MODBUS configuration has more registers than maximum allowed of %1');
    this.slaveDuplicatesMessage = this.translate.instant('The following slave definitions are duplicated');
    this.registerDuplicatesMessage = this.translate.instant('The following registers are duplicated');
    this.signalsDuplicatesMessage = this.translate.instant('The following signals are duplicated');
    this.duplicatedItemsMessage = this.translate.instant('The MODBUS configuration has duplicates items');
    this.errorTitle = this.translate.instant('Error');
    this.cancelButton = this.translate.instant('Cancel');
    this.importSuccessMessage = this.translate.instant('successfully imported configuration.');
  }

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.initializeComponent();
  }

  private initializeComponent(): void {
    this.isAdminOrSuperAdminOrTechAdmin = this.isAdminRole || this.isSuperAdminRole || this.isTechAdminRole;
    this.setModbusSettings();
    this.setSignalNames();
  }

  get formControls() {
    return this.modbusForm.controls;
  }

  onFileSelected(event: Event) {
    const file = this.importExportService.onFileSelected(event);
    if (!file) {
      return;
    }
    const fileReader = new FileReader();
    fileReader.readAsText(file, 'UTF-8');
    fileReader.onload = async () => {
      if (!fileReader.result) {
        this.notification.error(this.undefinedConfigurationMessage, null, this.COMPONENT_NAME, true);
        return;
      }
      const importedConfig = JSON.parse(String(fileReader.result));
      if (this.validateModbusConfig(importedConfig)) {
        this.parseAndImportConfig(importedConfig, this.settings);
        this.updateSignalNames(importedConfig);
        this.notification.success(this.importSuccessMessage, this.COMPONENT_NAME, true);
      }
    };
    if (this.input) {
      this.input.nativeElement.value = '';
    }
  }

  onExportModbusConfig() {
    const filename = this.importExportService.createModbusFilename(this.gateway.id);
    const details = this.importExportService.generateModbusExportDetails(this.gateway.id, this.settings);

    const hiddenElement = document.createElement('a');
    hiddenElement.href = 'data:text/json;charset=utf-8,' + encodeURI(details);
    hiddenElement.target = '_blank';
    hiddenElement.download = filename;
    hiddenElement.click();
  }

  save() {
    if (this.modbusForm.invalid || !this.formsAreValid()) {
      return;
    }
    if (!this.gatewaySignals || this.gatewaySignals.length < 1) {
      this.gatewaySignals = [new GatewaySignals()];
    }
    this.gatewaySignals[0]['@type'] = GATEWAY_CONFIG_DATATYPE;
    this.gatewaySignals[0].modbus = this.settings;
    this.saveConfigurationEvent.emit(this.gatewaySignals[0]);
  }

  buttonsDisabled() {
    return this.modbusForm.invalid || !this.formsAreValid() || !this.isAdminOrSuperAdminOrTechAdmin;
  }

  pushToIoTCore() {
    this.pushToIoTCoreEvent.emit();
  }

  addSlaveDefinition() {
    if (!this.settings.slaves) {
      this.settings.slaves = [];
    }
    const index = this.settings.slaves.length;
    const name = 'Slave ' + index;
    const slave: ModbusSlave = {
      deviceId: index + 1,
      name: name,
      readCode: this.READ_CODE_RANGE_MIN,
      registers: []
    };
    this.settings.slaves.push(slave);
  }

  onUpdateModbusSetting() {
    this.settings.serialPort.baudrate = this.formControls.baudRate.value;
    this.settings.serialPort.parity = this.parity.indexOf(this.formControls.parity.value);
    this.settings.serialPort.databits = this.formControls.dataBits.value;
    this.settings.serialPort.stopbits = this.formControls.stopBits.value;
    this.settings.polltimeMs = this.formControls.pollTime.value;
  }

  onRegisterFormIsValid(valid: boolean) {
    this.registerFormIsValid = valid;
  }

  onSignalFormIsValid(valid: boolean) {
    this.signalFormIsValid = valid;
  }

  onSlaveFormIsValid(valid: boolean) {
    this.slaveFormIsValid = valid;
  }

  onRemoveSlave(slave: ModbusSlave) {
    const index = this.settings.slaves.indexOf(slave);
    if (index < 0) {
      return;
    }
    this.settings.slaves.splice(index, 1);
  }

  private formsAreValid(): boolean {
    return this.registerFormIsValid && this.signalFormIsValid && this.slaveFormIsValid;
  }

  private setModbusSettings() {
    if (!this.gatewaySignals || this.gatewaySignals.length == 0) {
      this.settings = new ModbusSetting();
      this.generateModbusForm(this.settings);
      return;
    }

    if (!this.gatewaySignals[0].modbus) {
      this.settings = new ModbusSetting();
    } else {
      this.settings = this.gatewaySignals[0].modbus;
    }
    this.generateModbusForm(this.settings);

  }

  private generateModbusForm(settings: ModbusSetting) {
    this.modbusForm = new UntypedFormGroup({
      baudRate: new UntypedFormControl(settings.serialPort.baudrate),
      parity: new UntypedFormControl(this.parity[settings.serialPort.parity]),
      dataBits: new UntypedFormControl(settings.serialPort.databits, [Validators.required, Validators.min(this.DATA_BITS_RANGE_MIN), Validators.max(this.DATA_BITS_RANGE_MAX)]),
      stopBits: new UntypedFormControl(settings.serialPort.stopbits, [Validators.required, Validators.min(this.STOP_BITS_RANGE_MIN), Validators.max(this.STOP_BITS_RANGE_MAX)]),
      pollTime: new UntypedFormControl(settings.polltimeMs, [Validators.required, Validators.min(this.POLL_TIME_MIN)])
    });
    this.formGenerated = true;
  }

  private setSignalNames() {
    for (const slave of this.settings.slaves) {
      if (!slave.registers) {
        continue;
      }
      for (const register of slave.registers) {
        if (!register.signals) {
          continue;
        }
        for (const signal of register.signals) {
          if (!this.signalNames.includes(signal.name)) {
            this.signalNames.push(signal.name);
          }
        }
      }
    }
  }


  validateModbusConfig(newConfig: any) {
    if (!newConfig) {
      this.notification.error(this.undefinedConfigurationMessage, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (!newConfig.typeOfData || newConfig.typeOfData.trim().length === 0) {
      this.notification.error(this.undefinedConnectorMessage, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (newConfig.typeOfData !== MODBUS_TYPE_OF_DATA) {
      const message = this.invalidTypeOfDataMessage.replace('%1', newConfig.typeOfData).replace('%2', MODBUS_TYPE_OF_DATA);
      this.notification.error(message, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (!newConfig.slaves || newConfig.slaves.length === 0) {
      this.notification.error(this.noSlaveDefinitionMessage, null, this.COMPONENT_NAME, true);
      return false;
    }
    if (this.getNumberOfRegisters(newConfig) > MAX_NUMBER_OF_REGISTERS) {
      const message = this.maxRegistersReachedMessage.replace('%1', String(MAX_NUMBER_OF_REGISTERS));
      this.notification.error(message, null, this.COMPONENT_NAME, true);
      return false;
    }
    const duplicates = this.getSignalDuplicates(newConfig);
    if (duplicates && duplicates.length > 0) {
      this.notification.error(this.duplicatedItemsMessage, null, this.COMPONENT_NAME, true);
      this.confirmationDialogService.openConfirmationDialog(this.errorTitle, duplicates, null, this.cancelButton);
      return false;
    }

    return true;
  }

  private getSignalDuplicates(newConfig: ModbusSetting) {
    let result: string = '';

    const slaveConfig = {values: newConfig.slaves};
    const registerConfig: any = {values: []};
    const signalConfig: any = {values: []};
    for (const slave of newConfig.slaves) {
      for (const register of slave.registers) {
        registerConfig.values.push(register);
        for (const signal of register.signals) {
          signalConfig.values.push(signal);
        }
      }
    }
    const slavesDuplicates = this.importExportService.getDuplicatesForConnectorConfig(slaveConfig, 'name');
    const registerDuplicates = this.importExportService.getDuplicatesForConnectorConfig(registerConfig, 'address');
    const signalDuplicates = this.importExportService.getDuplicatesForConnectorConfig(signalConfig, 'name');

    const duplicatedSlaves = this.importExportService.generateDuplicatesValueEnumeration(slavesDuplicates);
    if (duplicatedSlaves && duplicatedSlaves.length > 0) {
      result += this.slaveDuplicatesMessage.toBold() + ': <br/>' + duplicatedSlaves + '</br>';
    }

    const duplicatedRegisters = this.importExportService.generateDuplicatesValueEnumeration(registerDuplicates);
    if (duplicatedRegisters && duplicatedRegisters.length > 0) {
      result += this.registerDuplicatesMessage.toBold() + ': <br/>' + duplicatedRegisters + '</br>';
    }

    const duplicatedSignals = this.importExportService.generateDuplicatesValueEnumeration(signalDuplicates);
    if (duplicatedSignals && duplicatedSignals.length > 0) {
      result += this.signalsDuplicatesMessage.toBold() + ': <br/>' + duplicatedSignals + '</br>';
    }

    return result.trim();
  }

  private getNumberOfRegisters(modbus: ModbusSetting) {
    let result = 0;
    if (!modbus) {
      return result;
    }
    if (!modbus.slaves) {
      return result;
    }

    for (const slave of modbus.slaves) {
      if (slave.registers) {
        result += slave.registers.length;
      }
    }
    return result;
  }

  private parseAndImportConfig(newConfig: ModbusSetting, settings: ModbusSetting) {
    settings.serialPort = this.ensureValidSerialPortSettings(newConfig.serialPort);
    this.formControls.baudRate.setValue(settings.serialPort.baudrate);
    this.formControls.parity.setValue(this.parity[settings.serialPort.parity]);
    this.formControls.dataBits.setValue(settings.serialPort.databits);
    this.formControls.stopBits.setValue(settings.serialPort.stopbits);

    settings.polltimeMs = this.updateSamplingRate(newConfig);
    this.formControls.pollTime.setValue(settings.polltimeMs);

    const newSlaves = newConfig.slaves;
    let existentSlaves = settings.slaves;
    for (const slave of newSlaves) {
      existentSlaves = this.updateSlaveDetails(slave, existentSlaves);
    }
    settings.slaves = existentSlaves;
  }

  private ensureValidSerialPortSettings(serialPort: ModbusSerialPort): ModbusSerialPort {
    if (!serialPort) {
      serialPort = new ModbusSerialPort();
    }
    serialPort.baudrate = this.ensureValidBaudRate(serialPort.baudrate);
    serialPort.parity = this.ensureValidParity(serialPort.parity);
    serialPort.databits = this.ensureValidDataBits(serialPort.databits);
    serialPort.stopbits = this.ensureValidStopBits(serialPort.stopbits);

    return serialPort;
  }

  private ensureValidBaudRate(baudRate: number): number {
    if (!baudRate) {
      return DEFAULT_BAUD_RATE;
    }
    if (Number.isNaN(baudRate)) {
      return DEFAULT_BAUD_RATE;
    }
    if (!BAUD_RATES.includes(baudRate)) {
      return DEFAULT_BAUD_RATE;
    }
    return baudRate;
  }

  private ensureValidParity(parity: number): number {
    if (!parity) {
      return DEFAULT_PARITY;
    }
    if (Number.isNaN(parity)) {
      return DEFAULT_PARITY;
    }
    if (!this.PARITY_OPTIONS.includes(parity)) {
      return DEFAULT_PARITY;
    }
    return parity;
  }

  private ensureValidDataBits(dataBits: number): number {
    if (!dataBits) {
      return DEFAULT_DATA_BITS;
    }
    if (Number.isNaN(dataBits)) {
      return DEFAULT_DATA_BITS;
    }
    if (dataBits < this.DATA_BITS_RANGE_MIN || dataBits > this.DATA_BITS_RANGE_MAX) {
      return DEFAULT_DATA_BITS;
    }
    return dataBits;
  }

  private ensureValidStopBits(stopBits: number): number {
    if (!stopBits) {
      return DEFAULT_STOP_BITS;
    }
    if (Number.isNaN(stopBits)) {
      return DEFAULT_STOP_BITS;
    }
    if (stopBits < this.STOP_BITS_RANGE_MIN || stopBits > this.STOP_BITS_RANGE_MAX) {
      return DEFAULT_STOP_BITS;
    }
    return stopBits;
  }

  private updateSlaveDetails(slave: ModbusSlave, existingSlaves: ModbusSlave[]): ModbusSlave[] {
    let existingSlave = this.findObjectInArrayByProperty('name', slave.name, existingSlaves);
    if (!existingSlave) {
      existingSlaves.push(slave);
      existingSlave = slave;
    }
    existingSlave.deviceId = slave.deviceId;
    existingSlave.readCode = slave.readCode;
    const newRegisters = slave.registers;
    let existingRegisters = existingSlave.registers;

    for (const register of newRegisters) {
      existingRegisters = this.updateRegisterDetails(register, existingRegisters);
    }
    existingSlave.registers = existingRegisters;
    return existingSlaves;
  }

  private updateRegisterDetails(newRegister: ModbusRegister, existingRegisters: ModbusRegister[]): ModbusRegister[] {
    let existingRegister = this.findObjectInArrayByProperty('address', newRegister.address, existingRegisters);
    if (!existingRegister) {
      existingRegisters.push(newRegister);
      existingRegister = newRegister;
    }
    let existingSignals = existingRegister.signals;
    const newSignals = newRegister.signals;

    for (const signal of newSignals) {
      existingSignals = this.updateSignalDetails(signal, existingSignals);
    }
    existingRegister.signals = existingSignals;
    return existingRegisters;
  }

  private updateSignalDetails(newSignal: ModbusSignal, existingSignals: ModbusSignal[]): ModbusSignal[] {
    let existingSignal = this.findObjectInArrayByProperty('name', newSignal.name, existingSignals);
    if (!existingSignal) {
      existingSignals.push(newSignal);
      existingSignal = newSignal;
    }
    existingSignal.order = newSignal.order;
    existingSignal.type = newSignal.type;
    existingSignal.bitmask = this.updateSignalBitMask(newSignal);

    return existingSignals;
  }

  private updateSignalBitMask(signal: ModbusSignal) {
    const limitsByType = this.getBitMaskLimitsByType(signal.type);
    if (signal.bitmask < limitsByType.min || signal.bitmask > limitsByType.max) {
      return limitsByType.max;
    }
    return signal.bitmask;
  }

  private getBitMaskLimitsByType(type: string) {
    let max = this.DATA_TYPE_MAX_VALUE;
    if (type === DATA_TYPE.INT16 || type === DATA_TYPE.UINT16) {
      max = this.SHORT_DATA_TYPE_MAX_VALUE;
    }

    return {min: 0, max: max};
  }

  private updateSamplingRate(config: ModbusSetting): number {
    const registers = this.getNumberOfRegisters(config);
    let calculatedSampleRate = registers * 1000 / 100;

    if (calculatedSampleRate < 100) {
      calculatedSampleRate = 100;
    }
    if (!config.polltimeMs || config.polltimeMs < calculatedSampleRate) {
      return calculatedSampleRate;
    }
    return config.polltimeMs;
  }

  private findObjectInArrayByProperty(propertyName: string, propertyValue: string | number, objectList: any[]) {
    // !propertyValue would exclude value 0
    if (!propertyName || !propertyValue || propertyValue === '' || !objectList || objectList.length === 0) {
      return null;
    }
    for (const object of objectList) {
      if (object[propertyName] === propertyValue) {
        return object;
      }
    }
    return null;
  }

  private updateSignalNames(config: ModbusSetting) {
    for (const slave of config.slaves) {
      for (const register of slave.registers) {
        for (const signal of register.signals) {
          if (signal.name) {
            if (!this.signalNames.includes(signal.name)) {
              this.signalNames.push(signal.name);
            }
          }
        }
      }
    }
    this.signalNames = [...this.signalNames];
  }

  onRemoveSignal(signal: ModbusSignal) {
    const index = this.signalNames.indexOf(signal.name);
    if (index < 0) {
      return;
    }
    this.signalNames.splice(index, 1);
  }

}
